import { motion } from 'framer-motion';
import { CSSProperties, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Row } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { removeFavoriteProfessional } from '../../../actions';
import defaultProfileImage from '../../../images/defaultProfileImage.png';
import { SolidXCircleIcon } from '@tempmee/react-icons';
import ConfirmPopup from '../../shared/ConfirmPopup';
import { IProfessionalCardUser } from '../interfaces';
import { FavoriteHeartButton } from './shared/FavoriteHeartButton';
import { ProfilePictureHover } from './shared/ProfilePictureHover';

// ── inline styles (no DS prop equivalent) ──────────────────────────────────
/**
 * The card frame: bespoke conditional surface (background, shadow, radii, fixed
 * legacy 700px width) on a motion.div — no DS Card matches these shapes.
 * NOTE(future-DS): the 'rgba(111,131,141,0.33)' card shadow has no DS shadow token.
 */
const getCardFrameStyle = ({
  isNewDesign,
  isAvailable,
  isFavoriteCard,
}: {
  isNewDesign?: boolean;
  isAvailable?: boolean;
  isFavoriteCard?: boolean;
}): CSSProperties =>
  isNewDesign
    ? {
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        position: 'relative',
        display: 'flex',
        backgroundColor: isFavoriteCard ? colors.favorites['30'] : colors.neutral['50'],
        borderRadius: 8,
        boxShadow: isAvailable || isFavoriteCard ? '0px 2px 4px 0px rgba(111, 131, 141, 0.33)' : '',
        alignItems: 'center',
        padding: '18px 0 14px 22px',
        maxHeight: isFavoriteCard ? 104 : undefined,
        minHeight: 80,
      }
    : {
        width: 700,
        height: 98,
        position: 'relative',
        display: 'flex',
        backgroundColor: colors.neutral['50'],
        borderRadius: '16px',
        borderTopLeftRadius: 84,
        borderBottomLeftRadius: 84,
        boxShadow: isAvailable ? '0px 2px 4px 0px rgba(111, 131, 141, 0.33)' : '',
      };
/** legacy avatar: grayscale/dim when unavailable + optional status ring; no DS Avatar equivalent */
const getLegacyAvatarStyle = (isAvailable: boolean, borderColor: string): CSSProperties => ({
  filter: isAvailable ? 'none' : 'grayscale(100%)',
  opacity: isAvailable ? 1 : 0.5,
  width: 98,
  height: 98,
  borderRadius: '50%',
  ...(borderColor && {
    border: `3px solid ${borderColor}`,
    width: 92,
    height: 92,
  }),
  boxShadow: '0px 3px 10px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
});
/** flex/minWidth/paddingRight (5px has no token) have no DS Box prop */
const getContentRowStyle = (hasHeartButton: boolean): CSSProperties => ({
  flex: 1,
  minWidth: 0,
  paddingRight: hasHeartButton ? 20 : 5,
});
/** flex has no DS Box prop */
const legacyContentRowStyle: CSSProperties = { flex: 1 };
/** un-styled button pinned to the card's top-right corner */
const removeButtonStyle: CSSProperties = {
  all: 'unset',
  position: 'absolute',
  top: -8,
  right: -8,
  backgroundColor: 'transparent',
  cursor: 'pointer',
};

interface IProfessionalCardProps {
  user: IProfessionalCardUser;
  Details: React.ComponentType<{ user: IProfessionalCardUser }>;
  Actions: React.ComponentType<{ user: IProfessionalCardUser }>;
  showRemoveIcon?: boolean;
  isNewDesign?: boolean;
  onFavorite?: () => void;
  isFavoriteCard?: boolean;
}

const getBorderColor = (isAvailable: boolean | undefined, isFavorite: boolean | undefined) => {
  if (isAvailable && isFavorite) {
    return colors.favorites['400'];
  }
  if (!isAvailable) {
    return colors.neutral['300'];
  }
  return '';
};

export const ProfessionalCard = ({
  user,
  Details,
  Actions,
  showRemoveIcon,
  isNewDesign,
  onFavorite,
  isFavoriteCard,
}: IProfessionalCardProps) => {
  const dispatch = useDispatch();
  const { isAvailable, isFavorite } = user;
  const borderColor = !isNewDesign ? getBorderColor(isAvailable, isFavorite) : '';
  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const handleShowRemoveModal = () => {
    setShowRemoveModal(!showRemoveModal);
  };

  const handleOnFavorite = () => {
    // Add a small delay to allow the heart animation to complete
    setTimeout(() => {
      onFavorite?.();
    }, 500);
  };

  return (
    <motion.div
      data-cy="professional-list-item"
      data-professional-id={user.id}
      key={user.id}
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        exit: { duration: 0.2 },
        layout: { duration: 0.3 },
      }}
      layout
      style={getCardFrameStyle({ isNewDesign, isAvailable, isFavoriteCard })}
    >
      {isNewDesign ? (
        <ProfilePictureHover
          id={user.id}
          profileImage={user.profileImage}
          isAvailable={isAvailable}
          isFavorite={isFavorite}
        />
      ) : (
        <img
          src={user.profileImage || defaultProfileImage}
          onError={(e) => {
            if (e.currentTarget.src !== defaultProfileImage) {
              e.currentTarget.src = defaultProfileImage;
            }
          }}
          alt="Profile"
          style={getLegacyAvatarStyle(Boolean(isAvailable), borderColor)}
        />
      )}

      {isNewDesign ? (
        <Row
          align="center"
          justify="between"
          style={getContentRowStyle(Boolean(onFavorite || isFavoriteCard))}
        >
          <Details user={user} />
          <Actions user={user} />
        </Row>
      ) : (
        <Row direction="row" justify="between" style={legacyContentRowStyle}>
          <Details user={user} />
          <Actions user={user} />
        </Row>
      )}

      {onFavorite && isNewDesign && (
        <FavoriteHeartButton onClick={handleOnFavorite} disabled={!isAvailable} />
      )}

      {isFavoriteCard && isNewDesign && (
        <FavoriteHeartButton
          onClick={handleShowRemoveModal}
          isFavorite
          isPendingRemoval={showRemoveModal}
        />
      )}

      {showRemoveIcon && (
        <button
          type="button"
          data-cy="remove-button"
          style={removeButtonStyle}
          onClick={handleShowRemoveModal}
        >
          <SolidXCircleIcon size={24} color={colors.secondary['500']} />
        </button>
      )}

      {showRemoveModal && (
        <ConfirmPopup
          title="Removal Confirmation"
          description={
            <>
              Please confirm that you would like to remove <b>{user.fullname}</b> from your
              Favorites List.
            </>
          }
          leftButtonText="Cancel"
          leftButtonAction={() => handleShowRemoveModal()}
          rightButtonText="Remove"
          rightButtonDataCy="remove-professional-confirm"
          rightButtonAction={() => {
            dispatch(removeFavoriteProfessional({ userId: user.id }));
            handleShowRemoveModal();
          }}
        />
      )}
    </motion.div>
  );
};
